import type {
  GetSubscriptionResponse,
  Plan,
  PlanFeatures,
  PlanInfo,
  PlanType,
} from '@otter-ai/recall-client';
import type { UpgradePlanType } from '@otter-ai/telemetry';
import { PLAN_TYPES_FROM_LOWEST_TO_HIGHEST_TIER } from '@otter-ai/zone-purchase/src/lib/DO_NOT_USE';
import { SALES_URL } from '@/constants/external-urls';
import { PATH_URL } from '@/constants/path-constants';
import { DAY_MS } from '@/constants/time';
import {
  type OtterFeatures,
  OtterPlanDiscountType,
  OtterPlanType,
  type SubscriptionPlanInfo,
} from '@/shared/types/plan';
import type { Subscription } from '../types/common';
import { convertSeconds } from './time/convertSeconds';

export const isProEduPlan = (plan: Plan) => {
  return (
    plan.type === OtterPlanType.PREMIUM &&
    plan.discount_type === OtterPlanDiscountType.ACADEMIC
  );
};

export const isValidPurchasePlan = (plan: Plan, selectedPlanType: string) => {
  return (
    plan.is_team &&
    plan.type === selectedPlanType &&
    plan.discount_type !== OtterPlanDiscountType.ACADEMIC
  );
};

export function isTeamPlan(plan: Plan) {
  return plan.type === OtterPlanType.TEAM;
}

export function isEnterprisePlan(plan: Plan) {
  return plan.type === OtterPlanType.ENTERPRISE;
}

export function isFreeTeamPlan(plan: Plan) {
  return plan.type === OtterPlanType.FREE_TEAM;
}

/**
 * Given a feature and a map from Otter feature to the plans that support it,
 * return the lowest tiered (cheapest) plan that supports the feature.
 *
 * @example
 * ```tsx
 * const lowestSupportedPlan = getLowestSupportedPlanForFeature('allow_dropbox_sync', featuresToPlansMap);
 *
 * return <div>Upgrade to {lowestSupportedPlan} to enable Dropbox Sync</div>;
 * ```
 *
 * @param feature - The feature to get the lowest supported plan for.
 * @param featuresToPlansMap - A map from Otter feature to the plans that support it.
 * @returns The lowest tiered (cheapest) plan that supports the feature.
 */
export function getLowestSupportedPlanForFeature(
  feature: OtterFeatures,
  featuresToPlansMap: PlanFeatures,
): OtterPlanType | null {
  const supportedPlans = getPlansForFeature(feature, featuresToPlansMap);
  return pickLowestTierPlan(supportedPlans as OtterPlanType[]);
}

/**
 * Given a feature and a map from Otter feature to the plans that support it,
 * return the plans that support the feature.
 *
 * @throws {Error} If the feature is not found or the plans are not an array.
 */
function getPlansForFeature(
  feature: OtterFeatures,
  featuresToPlansMap: PlanFeatures,
): OtterPlanType[] {
  const supportedPlans = featuresToPlansMap[feature];
  if (!supportedPlans) {
    throw new Error(`Feature ${feature} is not found`);
  }

  if (!Array.isArray(supportedPlans)) {
    throw new Error(`Expected an array of plan types for feature ${feature}`);
  }

  const otterPlanTypesSet = new Set<string>(Object.values(OtterPlanType));
  if (supportedPlans.some((value) => !otterPlanTypesSet.has(value))) {
    throw new Error(`Invalid plan type: ${supportedPlans.join(', ')}`);
  }

  return supportedPlans as OtterPlanType[];
}

/**
 * Given a list of plan types, return the lowest tiered (cheapest) plan.
 * @returns Lowest tiered (cheapest) plan
 */
function pickLowestTierPlan(plans: OtterPlanType[]): OtterPlanType | null {
  for (const plan of PLAN_TYPES_FROM_LOWEST_TO_HIGHEST_TIER) {
    if (plans.includes(plan)) {
      return plan;
    }
  }
  return null;
}

/**
 * Retrieve the purchase URL, upgrade text, and upgrade plan for a specific plan.
 */
export function getUpgradeDetails(planType: PlanType): {
  url: string;
  name: string;
  upgradePlan?: UpgradePlanType;
} {
  switch (planType) {
    // free plans, expired business, expired pro, and otter plan lite from referrals
    case 'free':
    case 'team_free':
    case 'team_free_pro':
    case 'reward':
      return {
        url: PATH_URL.PURCHASE_PRO_PLAN,
        name: 'Pro',
        upgradePlan: 'premium',
      };
    // pro plans and pro trials
    case 'premium':
    case 'premium_trial':
      return {
        url: PATH_URL.PURCHASE_TEAM_PLAN,
        name: 'Business',
        upgradePlan: 'team',
      };
    // business
    case 'team':
      return {
        url: SALES_URL,
        name: 'Enterprise',
        upgradePlan: 'enterprise',
      };
    default:
      return {
        url: `${PATH_URL.SETTINGS_PLAN}`,
        name: 'Upgrade',
      };
  }
}

/* Given list of plans and subscription, return the right plan that user is on */
export function getEffectivePlan(
  plans?: Plan[],
  subscription?: GetSubscriptionResponse['subscription'],
) {
  // Remove type assertion once recall-client is updated
  const effectivePlanId =
    (subscription?.new_plan as PlanInfo)?.id ?? subscription?.plan_id;

  return plans?.find((p) => {
    return p.id === effectivePlanId;
  });
}

export function getEffectivePlanInfo(
  subscription: GetSubscriptionResponse['subscription'],
) {
  return subscription.new_plan ?? subscription.plan;
}

export function getMonthlyPriceFromPlanInfo(
  planInfo: SubscriptionPlanInfo,
): number {
  if (planInfo.cycle === 'year') return Number(planInfo.price) / 12;
  return Number(planInfo.price);
}

export function getMonthlyPriceFromPlan(plan: Plan): number {
  if (plan.payment_period_type === 'year') return Number(plan.price) / 12;
  return Number(plan.price);
}

export function getImportLimit(
  subscription?: GetSubscriptionResponse['subscription'],
): number | 'unlimited' | 'three_lifetime' | null {
  if (subscription) {
    // if null from the BE, that signals unlimited imports
    if (subscription?.import_quota === null) {
      return 'unlimited';
    }
    // 0 means they have a lifetime import of 3
    if (subscription?.import_quota === 0) {
      return 'three_lifetime';
    }
    // a non-zero number is the monthly import limit
    if (subscription?.import_quota && Number(subscription.import_quota) > 0) {
      return Number(subscription.import_quota);
    }
  }
  return null;
}

/* check if user is eligible for dropbox export and other advanced features */
export function hasAdvancedFeatures(
  plans: Plan[],
  subscription: GetSubscriptionResponse['subscription'],
): boolean {
  const plan = getEffectivePlan(plans, subscription);
  if (!plan) {
    return false;
  }

  return (
    plan.type === OtterPlanType.PREMIUM ||
    plan.type === OtterPlanType.REWARD ||
    plan.type === OtterPlanType.TEAM ||
    plan.type === OtterPlanType.ENTERPRISE
  );
}

export function getSubscriptionUsage(subscription: Subscription) {
  const { seconds_left, seconds_quota, cycle_end_at } = subscription;
  const secondsUsed = Math.max(0, seconds_quota - seconds_left);
  const minutesUsed = convertSeconds(secondsUsed, 'minutes', 'round');
  const remainingMinutes = convertSeconds(seconds_left, 'minutes', 'floor');
  const remainingPercentage =
    seconds_quota > 0 ? seconds_left / seconds_quota : null;
  const hasRemainingQuota = seconds_quota > 0 ? seconds_left > 0 : true;
  const cycleEndDate = new Date(cycle_end_at * 1000);
  const msToCycleEnd = Math.max(0, cycleEndDate.getTime() - Date.now());
  const daysToCycleEnd = Math.ceil(msToCycleEnd / DAY_MS);
  return {
    minutesUsed,
    remainingMinutes,
    remainingPercentage,
    hasRemainingQuota,
    daysToCycleEnd,
    cycleEndDate,
  };
}
